import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch,useSelector } from 'react-redux';
import i18next from 'i18next';
import axios from 'axios';

import {getCompanyById} from '../utils/tenantManager.js'
import {setSettings} from '../store/settingSlice.js'
import {setLang} from '../store/langSlice.js'

import {arrayNameByLang,arrayToObjKeyValue, appStorageKey} from '../utils/global.js'
import useLicense from './useLicense.js';

const CENTRAL_SETTINGS_SCOPE = 'global';
const CENTRAL_SELECTION_SCOPE = 'header-selection';
const CENTRAL_SETTINGS_CACHE_PREFIX = 'centralSettingsCache:';

const useStartup = () => {
    
    const dispatch = useDispatch();
    const [startup,setStartup] = useState(false);
    const user = useSelector(state => state.auth).user
    const lang = useSelector(state => state.lang)
    const currentSettingsState = useSelector(state => state.settings);

    const safeParse = (value) => {
        if (!value) return null;
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    };
    const userSettings = arrayToObjKeyValue(safeParse(localStorage.getItem('user'))?.userSettings);
    const normalizeLang = (value) => String(value || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
    const storedPreferredLang = (() => {
        try {
            return localStorage.getItem('preferredLang') || localStorage.getItem('i18nextLng') || localStorage.getItem('appLang');
        } catch {
            return null;
        }
    })();
    const preferredLang = storedPreferredLang ? normalizeLang(storedPreferredLang) : null;
    const [centralSettings, setCentralSettings] = useState(null);
    const [centralLang, setCentralLang] = useState(null);
    const startupAppliedRef = useRef('');

    const userCompany = getCompanyById(user?.company_id)
    const storedCompany = safeParse(localStorage.getItem(appStorageKey('selectedCompany')));
    const storedCompanyId = storedCompany?.value;
    const storedCompanyValid = storedCompanyId && getCompanyById(storedCompanyId);
    const selectedCompany = storedCompanyValid || userCompany;

    const selectedCompanyName = selectedCompany && (
        arrayNameByLang(selectedCompany?.name, lang.value)?.text || 
        arrayNameByLang(selectedCompany?.name, lang.value === 'en' ? 'ar' : 'en')?.text ||
        'Company'
    );  
    const userBranch = userCompany?.branches?.find((branch) => branch?._id === user?.branch_id);
    const storedBranch = safeParse(localStorage.getItem(appStorageKey('selectedBranch')));
    const storedBranchId = storedBranch?.value;
    const companyBranches = selectedCompany?.branches || [];
    const storedBranchValid = storedBranchId && companyBranches?.some((branch) => branch?._id === storedBranchId);
    const selectedBranch = storedBranchValid
        ? companyBranches?.find((branch) => branch?._id === storedBranchId)
        : (selectedCompany?._id === userCompany?._id ? userBranch : companyBranches?.[0]);
    const selectedBranchName = selectedBranch && (
        arrayNameByLang(selectedBranch?.name, lang.value)?.text || 
        arrayNameByLang(selectedBranch?.name, lang.value === 'en' ? 'ar' : 'en')?.text ||
        'Branch'
    );
    const canReadCentralSettings = !selectedCompany?._id || user?.role === 'admin' || !user?.company_id || String(user.company_id) === String(selectedCompany._id);

    const invoiceSettings = selectedCompany ? arrayToObjKeyValue(selectedCompany?.settings?.invoice) : null;
    const generalSettings = selectedCompany ? arrayToObjKeyValue(selectedCompany?.settings?.generalSettings) : null;
    const quoteSettings = selectedCompany ? arrayToObjKeyValue(selectedCompany?.settings?.quote) : null;
    
    const mergedSettings = useMemo(() => ({
        invoiceSettings: centralSettings?.invoiceSettings || invoiceSettings,
        generalSettings: centralSettings?.generalSettings || generalSettings,
        quoteSettings: centralSettings?.quoteSettings || quoteSettings
    }), [centralSettings, generalSettings, invoiceSettings, quoteSettings]);
    const nextSettingsPayload = useMemo(() => ({
        invoiceSettings: mergedSettings.invoiceSettings || {},
        generalSettings: mergedSettings.generalSettings || {},
        quoteSettings: mergedSettings.quoteSettings || {}
    }), [mergedSettings.generalSettings, mergedSettings.invoiceSettings, mergedSettings.quoteSettings]);
    const currentSettingsSignature = useMemo(
        () => JSON.stringify({
            invoiceSettings: currentSettingsState?.invoiceSettings || {},
            generalSettings: currentSettingsState?.generalSettings || {},
            quoteSettings: currentSettingsState?.quoteSettings || {}
        }),
        [currentSettingsState?.generalSettings, currentSettingsState?.invoiceSettings, currentSettingsState?.quoteSettings]
    );
    const nextSettingsSignature = useMemo(
        () => JSON.stringify(nextSettingsPayload),
        [nextSettingsPayload]
    );
    
    const DEFAULT_LANG = mergedSettings?.generalSettings?.DEFAULT_LANG || userSettings?.DEFAULT_LANG || 'en';
    const langToApply = useMemo(() => {
        return preferredLang || (lang?.value ? normalizeLang(lang.value) : null) || (centralLang ? normalizeLang(centralLang) : null) || normalizeLang(DEFAULT_LANG);
    }, [DEFAULT_LANG, centralLang, lang?.value, preferredLang]);
    
    const {data:dataLicense} = useLicense();

    useEffect(() =>{
        const pathArray = window.location.pathname.split('/');
        if(pathArray.length > 2 && pathArray[pathArray.length-2] === 'token'){
            setStartup(true)
        }
    },[dataLicense]);
    
    useEffect(()=>{
        if(selectedCompany){
            const applyKey = [
                selectedCompany?._id || '',
                selectedBranch?._id || '',
                langToApply || '',
                nextSettingsSignature || ''
            ].join('|');
            if (startupAppliedRef.current === applyKey && startup) {
                return;
            }

            if (lang?.value !== langToApply) {
                dispatch(setLang({
                    label : langToApply === 'ar' ? 'عربي' : langToApply === 'en' ? 'English' : langToApply,
                    value : langToApply
                }));
            }

            if (i18next.language !== langToApply) {
                i18next.changeLanguage(langToApply);
            }

            if (currentSettingsSignature !== nextSettingsSignature) {
                dispatch(setSettings(nextSettingsPayload));
            }

            if (!startup) {
                setStartup(true)
            }
            startupAppliedRef.current = applyKey;
        }

    },[currentSettingsSignature, dispatch, lang?.value, langToApply, nextSettingsPayload, nextSettingsSignature, selectedBranch?._id, selectedCompany?._id, startup]);

    useEffect(() => {
        const fetchCentralSettings = async () => {
            if (!selectedCompany?._id || !user?.tenant_id) return;
            if (!canReadCentralSettings) {
                setCentralSettings(null);
                return;
            }
            const token = localStorage.getItem('token');
            if (!token) return;
            const cacheKey = `${CENTRAL_SETTINGS_CACHE_PREFIX}${user.tenant_id}:${selectedCompany._id}:${CENTRAL_SETTINGS_SCOPE}`;
            const cachedRaw = safeParse(localStorage.getItem(cacheKey));
            if (cachedRaw?.data) {
                setCentralSettings(cachedRaw.data);
            }
            const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&companyId=${selectedCompany._id}&scope=${CENTRAL_SETTINGS_SCOPE}`;
            try {
                const headers = {
                    Authorization: `Bearer ${token}`
                };
                if (cachedRaw?.version) {
                    headers['If-None-Match'] = String(cachedRaw.version);
                }
                const response = await axios.get(url, { headers, validateStatus: () => true });
                if (response.status === 304) {
                    return;
                }
                if (response?.data?.status === 1) {
                    const nextData = response?.data?.data || null;
                    const nextVersion = response?.data?.version ?? cachedRaw?.version ?? 0;
                    setCentralSettings(nextData);
                    localStorage.setItem(cacheKey, JSON.stringify({ data: nextData, version: nextVersion }));
                    return;
                }
                if (response?.status === 403 || response?.status === 401) {
                    setCentralSettings(cachedRaw?.data || null);
                }
            } catch (error) {
            }
        };
        fetchCentralSettings();
    }, [canReadCentralSettings, selectedCompany?._id, user?.tenant_id]);

    useEffect(() => {
        const fetchCentralSelection = async () => {
            if (!user?.tenant_id) return;
            const token = localStorage.getItem('token');
            if (!token) return;
            const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&scope=${CENTRAL_SELECTION_SCOPE}`;
            try {
                const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true });
                if (response?.data?.status !== 1) {
                    setCentralLang(null);
                    return;
                }
                const selection = response?.data?.data;
                const selectedLang = selection?.selectedLang || selection?.lang || selection?.language || response?.data?.currentLanguage;
                setCentralLang(selectedLang || null);
            } catch (error) {
                setCentralLang(null);
            }
        };
        fetchCentralSelection();
    }, [user?.tenant_id]);

    return {startup}
}

export default useStartup
